<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import GroupStatsHead from '@/components/Stats/GroupStats/GroupStatsHead.vue'
import GroupSubjectInsights from '@/components/Stats/GroupStats/GroupSubjectInsights.vue'
import SectionHead from '@/components/Stats/SectionHead.vue'
import ProgressBar from '@/components/Stats/ProgressBar.vue'
import NoSeatsBanner from '@/components/NoSeatsBanner.vue'
import { groupModule } from '@/store/group/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import GroupQuestionTable from '@/components/GroupQuestionTable.vue'
import GroupSubjectTable from '@/components/Stats/GroupStats/GroupSubjectTable.vue'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'
import { licenseModule } from '@/store/license/module'
import { analyticsModule } from '@/store/analytics/module'

const route = useRoute()

const {
    SegmentControl,
} = UIKit

const questionOrSubject = ref<'By Question' | 'By Subject'>('By Question')
const flagged = ref(true)
const componentKey = ref(0) // For re-rendering the progress bar when user swap groups
const totalProgress = ref(0)
const progressComplete = ref(false)
const showPurchaseSeatsModal = ref(false)

const groupStats = computed(() => statsModule.getters.getCurrentGroupAggStats())

const isLoading = computed(() => groupStats.value.isLoading)

const flaggedCount = computed(() => {
    return groupStats.value?.questionStats.filter(q => !q.isMockQuestion && q.numStudentsFlagged > 0).length
})

const examGuidLicenseLib = computed(() => licenseModule.getters.getExamGuidAvailableLicenseLib())

const isExamMissingLicenses = computed(() => {
    const currentExamGuid = examModule.getters.getCurrentExamMetadata()?.examGuid
    if (!currentExamGuid) {
        return false
    }
    return !examGuidLicenseLib.value[currentExamGuid]
})

const basicStudents = computed(() => groupModule.getters.getBasicStudents())

const checkLoading = () => {
    if (!isLoading.value) {
        // No need to poll if things are already loaded
        progressComplete.value = true
    } else {
        const timer = window.setInterval(() => {
            if (isLoading.value && totalProgress.value < 99) {
                // Increment just short of 100 so it doesn't look like loading is done if it's not
                totalProgress.value++
            } else if (!isLoading.value && totalProgress.value < 100) {
                totalProgress.value = 100
                clearInterval(timer)
                window.setTimeout(() => {
                    progressComplete.value = true
                }, 500) // Should match the css transition time
            }
        }, 100)
    }
}

const byQuestionOrSubject = (type: number) => {
    const questionOrSubjectVal = type === 1 ? 'By Question' : 'By Subject'
    questionOrSubject.value = questionOrSubjectVal

    analyticsModule.actions.amplitudeTrack('Segment Control Clicked', {
        segmentControlContext: 'Group Stats Flagged Questions',
        segmentControlValue: questionOrSubjectVal,
    })
}

const closePurchaseSeatsModal = () => {
    showPurchaseSeatsModal.value = false
}

onBeforeRouteUpdate((to, from, next) => {
    // Re-initialize all our stuff to maintain progress state
    componentKey.value += 1
    totalProgress.value = 0
    progressComplete.value = false

    groupModule.actions.updateCurrentGroupId(to.params.groupId as string)
    studentModule.actions.updateCurrentStudentId(null)
    statsModule.actions.fetchCurrentGroupAggStats()
    examModule.actions.fetchSubjectsWithLevels()
    // Start polling on swap
    checkLoading()
    next()
})

onMounted(async () => {
    // Start polling to see if we are loading or not
    checkLoading()

    const routeGroupId = route.params.groupId
    await Promise.all([
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        studentModule.actions.updateCurrentStudentId(null),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
})
</script>

<template>
    <div class="group-stats">
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="closePurchaseSeatsModal"
        />
        <GroupStatsHead />
        <ProgressBar
            v-if="!progressComplete"
            :key="componentKey"
            class="group-stats__progress-bar"
            aria-label="Loading group stats"
            :aria-valuenow="totalProgress"
            :aria-valuemin="0"
            :aria-valuemax="100"
            role="progressbar"
            tabindex="0"
            :percent-progress="totalProgress"
        />
        <NoSeatsBanner
            class="group-stats__no-available-seats-banner"
            v-if="progressComplete && isExamMissingLicenses && basicStudents.length"
            :num-students-missing-licenses="basicStudents.length"
        />
        <GroupSubjectInsights
            v-show="progressComplete"
        />
        <SectionHead
            v-if="progressComplete"
            class="group-stats__group-flagged-header"
            :title="`Flagged Questions <span>(${flaggedCount})</span>`"
            :flagged="flagged"
            icon="flag"
        >
            <SegmentControl
                :default-option="questionOrSubject === 'By Question' ? 1 : 2"
                option1="By Question"
                option2="By Subject"
                @change="byQuestionOrSubject"
            />
        </SectionHead>
        <GroupQuestionTable
            v-if="progressComplete && questionOrSubject === 'By Question'"
        />
        <GroupSubjectTable
            v-if="progressComplete && questionOrSubject === 'By Subject'"
        />
    </div>
</template>

<style lang="scss" scoped>
.group-stats {
    :deep(*) {
        .banner__text {
            margin: 0;
        }
    }

    &__group-flagged-header {
        margin-bottom: 13px;
    }
}
</style>
